import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { locationHubService } from '../../services/locationHubService';
import Loader from '../common/Loader.js';
import { destinationTypeLabel } from '../../utils/destinationType.js';

const PERMISSION_FIELDS = [
  ['allow_paid', 'Paid'],
  ['allow_to_pay', 'To Pay'],
  ['allow_on_account', 'On Account'],
  ['allow_delivery', 'Delivery'],
  ['allow_booking', 'Booking'],
];

const DetailCell = ({ label, children, className = '' }) => (
  <div className={`bg-gray-50 rounded-xl p-3 border border-gray-200 ${className}`}>
    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</p>
    <div className="text-sm font-semibold text-secondary break-words">{children}</div>
  </div>
);

const ViewLocationHubModal = ({ hubId, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [hub, setHub] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchHub = async () => {
      if (!hubId) return;
      try {
        setLoading(true);
        setError('');
        const response = await locationHubService.getHub(hubId);
        const data = response.data.hub || response.data.data;
        if (!cancelled) {
          if (data) setHub(data);
          else setError('Location hub not found');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load location hub');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHub();
    return () => { cancelled = true; };
  }, [hubId]);

  const dispatchTypes = Array.isArray(hub?.dispatch_types)
    ? hub.dispatch_types
    : hub?.dispatch_types
      ? String(hub.dispatch_types).split(',').map((v) => v.trim()).filter(Boolean)
      : ['OGCL'];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">
              {t('pages.locationHub.detailsTitle', { defaultValue: 'Location Hub Details' })}
            </h3>
            {hub && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {hub.location} · {hub.hub_id}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 shrink-0">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader fullScreen={false} size="default" message={t('pages.locationHub.loading')} />
            </div>
          ) : error ? (
            <p className="text-center text-red-600 py-10">{error}</p>
          ) : hub ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <DetailCell label={t('pages.locationHub.hubBranchColumn', { defaultValue: 'Hub/Branch' })}>{hub.location}</DetailCell>
                <DetailCell label={t('pages.locationHub.hubCode')}>{hub.location_code}</DetailCell>
                <DetailCell label="Ownership">
                  <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-semibold ${
                    (hub.ownership_type || 'OWN') === 'AGENT'
                      ? 'bg-amber-50 text-amber-800 border border-amber-200'
                      : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                  }`}>
                    {hub.ownership_type || 'OWN'}
                  </span>
                </DetailCell>
                <DetailCell label="Dispatch Types">
                  <div className="flex flex-wrap gap-1.5">
                    {dispatchTypes.map((type) => (
                      <span
                        key={type}
                        className={`inline-flex px-2 py-0.5 rounded-md text-xs font-semibold border ${
                          type === 'ITCL'
                            ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                            : 'bg-sky-50 text-sky-800 border-sky-200'
                        }`}
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                </DetailCell>
                <DetailCell label="Destination Type">
                  {(hub.destination_type_label || destinationTypeLabel(hub.destination_type) || '—').toUpperCase()}
                </DetailCell>
                <DetailCell label={t('forms.state')}>{hub.state || '—'}</DetailCell>
                <DetailCell label="Parent Hub">
                  {hub.parent_hub_name
                    ? `${hub.parent_hub_name}${hub.parent_hub_code ? ` (${hub.parent_hub_code})` : ''}`
                    : '—'}
                </DetailCell>
                <DetailCell label={t('reports.avgCommissionPct')}>
                  {hub.commission_percentage != null ? `${hub.commission_percentage}%` : 'N/A'}
                </DetailCell>
                <DetailCell label="Rate / KG">
                  {hub.rate_per_kg != null && hub.rate_per_kg !== '' ? `₹${hub.rate_per_kg}` : 'N/A'}
                </DetailCell>
                <DetailCell label="Mobile No.">{hub.location_hub_contact || 'N/A'}</DetailCell>
                <DetailCell label={t('common.address')} className="col-span-2">
                  {hub.address || '—'}
                </DetailCell>
              </div>

              <div>
                <p className="text-sm font-semibold text-secondary mb-2">Payment & Delivery Permissions</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {PERMISSION_FIELDS.map(([key, label]) => (
                    <div key={key} className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</p>
                      <p className={`text-sm font-semibold ${hub[key] ? 'text-green-700' : 'text-red-600'}`}>
                        {hub[key] ? 'Allowed' : 'Not allowed'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ViewLocationHubModal;
